import * as THREE from 'three';
import type { Input } from '../engine/Input';
import type { Game, Tickable } from '../engine/Game';
import { bus } from '../engine/EventBus';
import { save, type PlacedFurniture } from '../engine/SaveSystem';
import { findFurniture } from '../data/furniture';
import { FURNITURE_BUILDERS } from './FurnitureMesh';

const GRID = 0.5;
const HOUSE_HALF = 3.9; // interior half-size (matches 8m floor minus walls)

interface Placed {
  data: PlacedFurniture;
  mesh: THREE.Group;
}

export class BuildMode implements Tickable {
  readonly container = new THREE.Group();
  private placed: Placed[] = [];
  private ghost: THREE.Group | null = null;
  private selectedId: string | null = null;
  private pendingRotY = 0;
  active = false;
  private raycaster = new THREE.Raycaster();
  private floorHit = new THREE.Plane(new THREE.Vector3(0, 1, 0), -0.2); // house floor Y
  private housePlot: { x: number; z: number; y: number };

  constructor(private game: Game, private input: Input, housePlot: { x: number; z: number; y: number }) {
    this.housePlot = housePlot;
    this.floorHit = new THREE.Plane(new THREE.Vector3(0, 1, 0), -(housePlot.y + 0.2));
    this.rebuildFromSave();
  }

  rebuildFromSave(): void {
    for (const p of this.placed) this.container.remove(p.mesh);
    this.placed = [];
    for (const item of save.data.placedFurniture) {
      const builder = FURNITURE_BUILDERS[item.id];
      if (!builder) continue;
      const mesh = builder();
      mesh.position.set(item.x, this.housePlot.y + 0.2, item.z);
      mesh.rotation.y = item.rotY;
      this.container.add(mesh);
      this.placed.push({ data: item, mesh });
    }
  }

  enter(): void {
    this.active = true;
    this.game.setMode('build');
    bus.emit('toast', { message: '🏡 Build mode — choose a piece and click to place.' });
    bus.emit('hint:set', { text: 'LMB place · R rotate · Right-click remove · B exit' });
  }

  exit(): void {
    this.active = false;
    this.clearGhost();
    this.selectedId = null;
    this.game.setMode('play');
    bus.emit('hint:set', { text: null });
  }

  selectItem(id: string): void {
    if (!save.data.unlockedFurniture.includes(id)) return;
    this.selectedId = id;
    this.pendingRotY = 0;
    this.buildGhost();
  }

  private buildGhost(): void {
    this.clearGhost();
    if (!this.selectedId) return;
    const builder = FURNITURE_BUILDERS[this.selectedId];
    if (!builder) return;
    const g = builder();
    g.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (mesh.material) {
        const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        for (const m of mats) {
          const mm = m as THREE.MeshStandardMaterial;
          mm.transparent = true;
          mm.opacity = 0.5;
          mm.depthWrite = false;
        }
      }
    });
    this.ghost = g;
    this.container.add(g);
  }

  private clearGhost(): void {
    if (this.ghost) {
      this.container.remove(this.ghost);
      this.ghost = null;
    }
  }

  tick(_dt: number, _elapsed: number): void {
    if (!this.active) return;

    // Toggle B handled by outer. Here: move ghost, place, rotate, remove.
    if (this.input.consumePress('r')) {
      this.pendingRotY += Math.PI / 2;
      if (this.ghost) this.ghost.rotation.y = this.pendingRotY;
    }

    if (!this.ghost) return;

    // Raycast from camera center to floor plane.
    const cam = this.game.camera;
    const dir = new THREE.Vector3();
    cam.getWorldDirection(dir);
    this.raycaster.set(cam.position, dir);
    const hit = new THREE.Vector3();
    if (!this.raycaster.ray.intersectPlane(this.floorHit, hit)) return;

    // Snap to grid, clamp to inside house.
    const localX = hit.x - this.housePlot.x;
    const localZ = hit.z - this.housePlot.z;
    const snappedX = Math.round(localX / GRID) * GRID;
    const snappedZ = Math.round(localZ / GRID) * GRID;
    const cx = THREE.MathUtils.clamp(snappedX, -HOUSE_HALF + 0.5, HOUSE_HALF - 0.5);
    const cz = THREE.MathUtils.clamp(snappedZ, -HOUSE_HALF + 0.5, HOUSE_HALF - 0.5);

    this.ghost.position.set(this.housePlot.x + cx, this.housePlot.y + 0.2, this.housePlot.z + cz);
    this.ghost.rotation.y = this.pendingRotY;

    // Place
    if (this.input.consumeMouse('left')) {
      const data: PlacedFurniture = {
        id: this.selectedId!,
        x: this.housePlot.x + cx,
        z: this.housePlot.z + cz,
        rotY: this.pendingRotY,
      };
      const builder = FURNITURE_BUILDERS[this.selectedId!];
      const mesh = builder();
      mesh.position.set(data.x, this.housePlot.y + 0.2, data.z);
      mesh.rotation.y = data.rotY;
      this.container.add(mesh);
      this.placed.push({ data, mesh });
      save.data.placedFurniture.push(data);
      bus.emit('furniture:placed', { id: data.id });
      bus.emit('save:dirty', {});
      const label = findFurniture(data.id)?.label ?? 'item';
      bus.emit('toast', { message: `✨ Placed ${label}` });
    }

    // Remove nearest with right click
    if (this.input.consumeMouse('right')) {
      const worldHit = new THREE.Vector3(this.housePlot.x + cx, this.housePlot.y + 0.2, this.housePlot.z + cz);
      let bestI = -1;
      let bestD = 1.2; // grab radius
      for (let i = 0; i < this.placed.length; i++) {
        const d = this.placed[i].mesh.position.distanceTo(worldHit);
        if (d < bestD) { bestD = d; bestI = i; }
      }
      if (bestI >= 0) {
        const p = this.placed[bestI];
        this.container.remove(p.mesh);
        this.placed.splice(bestI, 1);
        const saveIdx = save.data.placedFurniture.indexOf(p.data);
        if (saveIdx >= 0) save.data.placedFurniture.splice(saveIdx, 1);
        bus.emit('furniture:removed', { index: saveIdx });
        bus.emit('save:dirty', {});
        bus.emit('toast', { message: '💨 Removed' });
      }
    }
  }
}
